import React, { useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import Header from '../dashboard/components/Header';
import Sidebar from '../dashboard/components/Sidebar';
import { createResource,getResources,deleteResource, updateResource} from '../../router/api';


interface SessionCaisse {
  id: number;
  date_ouverture: string;
  heure_ouverture: string;
  solde_ouverture: number;
  total_encaissements: number;
  total_decaissements: number;
  solde_theorique: number;
}

interface OperationCaisse {
  id: number;
  type: 'ENCAISSEMENT' | 'DECAISSEMENT';
  categorie: string;
  sous_categorie?: string;
  montant: number;
  description?: string;
  document_reference?: string;
  date_operation: string;
  heure_operation?: string;
}

export default function CashRegisterPage() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'opening' | 'operations' | 'closing'>('opening');

  const [currentSession, setCurrentSession] = useState<SessionCaisse | null>(null);
  const [operations, setOperations] = useState<OperationCaisse[]>([]);
  const [loading, setLoading] = useState(true);

  // Formulaire ouverture
  const [openingBalance, setOpeningBalance] = useState('');

  // Formulaire opération
  const [showOperationForm, setShowOperationForm] = useState(false);
  const [operationType, setOperationType] = useState<'ENCAISSEMENT' | 'DECAISSEMENT'>('ENCAISSEMENT');
  const [operationCategory, setOperationCategory] = useState('');
  const [operationSubcategory, setOperationSubcategory] = useState('');
  const [operationAmount, setOperationAmount] = useState('');
  const [operationDescription, setOperationDescription] = useState('');
  const [operationReference, setOperationReference] = useState('');

  // Formulaire clôture
  const [actualBalance, setActualBalance] = useState('');
  const [justification, setJustification] = useState('');
  const [closingLoading, setClosingLoading] = useState(false);

  // Catégories exactes comme dans ton design
  const encaissementCategories = [
    { label: 'Ventes au Comptant', value: 'vente_comptant', description: 'Factures clients payées en espèces', requiresLink: true },
    { label: 'Règlements Clients', value: 'reglement_client', description: 'Paiement de créances clients', requiresLink: true },
    { label: 'Autres Recettes', value: 'autres_recettes', description: 'Remboursements, recettes diverses', subcategories: ['Remboursement de frais', 'Vente d\'actifs', 'Autres'] }
  ];

  const decaissementCategories = [
    { label: 'Règlements Fournisseurs', value: 'reglement_fournisseur', description: 'Paiement de factures fournisseurs', requiresLink: true },
    { label: 'Autres Dépenses/Frais', value: 'autres_depenses', description: 'Petites dépenses courantes', subcategories: ['Fournitures de bureau', 'Transport et déplacement', 'Emballage et conditionnement', 'Entretien et réparations', 'Frais postaux', 'Petites fournitures', 'Autres frais'] },
    { label: 'Versements/Transferts', value: 'versement_banque', description: 'Transfert vers compte bancaire', requiresLink: false }
  ];

  // Calculs

  const totalEncaissements = operations.filter(op => op.type === 'ENCAISSEMENT').reduce((sum, op) => sum + op.montant, 0);
  const totalDecaissements = operations.filter(op => op.type === 'DECAISSEMENT').reduce((sum, op) => sum + op.montant, 0);
  const theoreticalBalance = currentSession?.solde_theorique || 0;
  const difference = actualBalance ? theoreticalBalance - parseFloat(actualBalance || '0') : 0;

  const selectedCategory = operationType === 'ENCAISSEMENT'
    ? encaissementCategories.find(c => c.value === operationCategory)
    : decaissementCategories.find(c => c.value === operationCategory);

  /* ================= CHARGEMENT SESSION & OPÉRATIONS ================= */
useEffect(() => {
  const loadData = async () => {
    setLoading(true);
    try {
      const sessionRes = await getResources(`caisses/session-active`);
      console.log(sessionRes.data);

      if (sessionRes.data) {
        const session = sessionRes.data;

        // 🔥 NORMALISATION DES DONNÉES
        setCurrentSession({
          ...session,
          solde_ouverture: Number(session.solde_ouverture),
          total_encaissements: Number(session.total_encaissements),
          total_decaissements: Number(session.total_decaissements),
          solde_theorique: Number(session.solde_theorique),
          solde_reel: session.solde_reel !== null ? Number(session.solde_reel) : null,
          ecart: session.ecart !== null ? Number(session.ecart) : null,
        });

        setActiveTab('operations');

const opsRes = await getResources(`caisses/${sessionRes.data.id}/operations`);

const normalizedOps = (Array.isArray(opsRes.data) ? opsRes.data : []).map(op => ({
  ...op,
  montant: Number(op.montant),
}));

setOperations(normalizedOps);
      }
    } catch (err: any) {
      if (err.response?.status !== 404) {
        toast.error('Erreur lors du chargement');
      }
    } finally {
      setLoading(false);
    }
  };

  loadData();
}, []);


  /* ================= OUVERTURE DE CAISSE ================= */
  const handleOpenCash = async () => {
    if (!openingBalance || parseFloat(openingBalance) < 0) {
      toast.error('Veuillez saisir un solde d\'ouverture valide');
      return;
    }

    try {
      const res = await createResource(`caisses/ouvrir`, {
        solde_ouverture: parseFloat(openingBalance)
      });

      setCurrentSession(res.data);
      setOperations([]);
      setActiveTab('operations');
      toast.success('Caisse ouverte avec succès');
      setOpeningBalance('');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Erreur lors de l\'ouverture de la caisse');
    }
  };

  /* ================= AJOUT D'OPÉRATION ================= */
  const handleAddOperation = async () => {
    if (!operationCategory || !operationAmount || parseFloat(operationAmount) <= 0) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    if (selectedCategory?.requiresLink && !operationReference.trim()) {
      toast.error('Cette catégorie nécessite une référence de document');
      return;
    }

    try {
      const res = await createResource(`caisses/operations`, {
        session_caisse_id: currentSession?.id,
        type: operationType,
        categorie: operationCategory,
        sous_categorie: operationSubcategory || null,
        montant: parseFloat(operationAmount),
        description: operationDescription.trim() || null,
        document_reference: operationReference.trim() || null,
      });

      setOperations([res.data, ...operations]);
      toast.success('Opération enregistrée avec succès');

      // Mise à jour du solde théorique
      const sessionRes = await getResources(`caisses/session-active`);
      setCurrentSession(sessionRes.data);

      // Reset formulaire
      setOperationCategory('');
      setOperationSubcategory('');
      setOperationAmount('');
      setOperationDescription('');
      setOperationReference('');
      setShowOperationForm(false);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Erreur lors de l\'enregistrement');
    }
  };

  /* ================= CLÔTURE DE CAISSE ================= */
  const handleCloseCash = async () => {
    if (!actualBalance || parseFloat(actualBalance) < 0) {
      toast.error('Veuillez saisir le solde réel compté');
      return;
    }

    if (Math.abs(difference) > 0.01 && !justification.trim()) {
      toast.error('Un écart a été détecté. Veuillez fournir une justification');
      return;
    }

    setClosingLoading(true);
    try {
      await createResource(`caisses/cloturer`, {
        session_caisse_id: currentSession?.id,
        solde_reel: parseFloat(actualBalance),
        justification_ecart: justification.trim() || null,
      });

      toast.success('Caisse clôturée avec succès !');
      setCurrentSession(null);
      setOperations([]);
      setActualBalance('');
      setJustification('');
      setActiveTab('opening');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Erreur lors de la clôture');
    } finally {
      setClosingLoading(false);
    }
  };

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className="flex-1 flex flex-col lg:ml-64">
        <Header onMenuClick={() => setSidebarOpen(true)} />
        <main className="flex-1 overflow-y-auto pt-24 pb-6">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* En-tête de la page */}
            <div className="mb-6">
              <div className="flex items-center gap-3 mb-2">
                <i className="ri-cash-line text-3xl text-teal-600"></i>
                <h1 className="text-3xl font-bold text-gray-900">Caisse</h1>
              </div>
              <p className="text-gray-600">Gérez vos transactions de caisse et vos encaissements</p>
            </div>

            {/* Statut de la caisse */}
            <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className={`w-4 h-4 rounded-full ${currentSession ? 'bg-green-500' : 'bg-red-500'}`}></div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      Statut : {currentSession ? 'Caisse Ouverte' : 'Caisse Fermée'}
                    </h3>
                    {currentSession && (
                      <p className="text-sm text-gray-600">
                        Ouverte le {currentSession.date_ouverture}
                        {currentSession.heure_ouverture
                          ? ` à ${currentSession.heure_ouverture.substring(0, 5)}`
                          : ''}
                      </p>

                    )}
                    
                  </div>
                </div>
                {currentSession && (
                  <div className="grid grid-cols-3 gap-6">
                    <div className="text-center">
                      <p className="text-sm text-gray-600">Solde d'ouverture</p>
                      <p className="text-xl font-bold text-gray-900">
                      <p className="text-xl font-bold text-gray-900">
                        {(currentSession.solde_ouverture ?? 0).toFixed(2)} GNF
                      </p>
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-gray-600">Solde actuel</p>
                      <p className="text-xl font-bold text-blue-600">
                        {theoreticalBalance.toFixed(2)} GNF
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-gray-600">Opérations</p>
                      <p className="text-xl font-bold text-gray-900">
                        {operations.length}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Navigation par onglets */}
            <div className="bg-white rounded-lg shadow-sm mb-6">
              <div className="border-b border-gray-200">
                <nav className="flex -mb-px">
                  <button
                    onClick={() => setActiveTab('opening')}
                    disabled={!!currentSession}
                    className={`px-6 py-4 text-sm font-medium border-b-2 whitespace-nowrap ${
                      activeTab === 'opening'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    } ${currentSession ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                  >
                    <i className="ri-door-open-line mr-2"></i>
                    Ouverture de Caisse
                  </button>
                  <button
                    onClick={() => setActiveTab('operations')}
                    disabled={!currentSession}
                    className={`px-6 py-4 text-sm font-medium border-b-2 whitespace-nowrap ${
                      activeTab === 'operations'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    } ${!currentSession ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                  >
                    <i className="ri-exchange-line mr-2"></i>
                    Opérations de Caisse
                  </button>
                  <button
                    onClick={() => setActiveTab('closing')}
                    disabled={!currentSession}
                    className={`px-6 py-4 text-sm font-medium border-b-2 whitespace-nowrap ${
                      activeTab === 'closing'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    } ${!currentSession ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                  >
                    <i className="ri-door-closed-line mr-2"></i>
                    Clôture de Caisse
                  </button>
                </nav>
              </div>

              <div className="p-6">
                {/* Onglet Ouverture */}
                {activeTab === 'opening' && !currentSession && (
                  <div className="max-w-2xl mx-auto">
                    <div className="text-center mb-8">
                      <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <i className="ri-door-open-line text-4xl text-blue-600"></i>
                      </div>
                      <h2 className="text-2xl font-bold text-gray-900 mb-2">
                        Ouverture de Caisse
                      </h2>
                      <p className="text-gray-600">
                        Saisissez le solde d'ouverture pour démarrer une nouvelle session de caisse
                      </p>
                    </div>

                    <div className="bg-gray-50 rounded-lg p-6 mb-6">
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Solde d'Ouverture (GNF) *
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={openingBalance}
                          onChange={(e) => setOpeningBalance(e.target.value)}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg"
                          placeholder="0.00"
                        />
                        <p className="text-sm text-gray-500 mt-2">
                          <i className="ri-information-line mr-1"></i>
                          Montant présent dans la caisse au début de la journée
                        </p>
                      </div>

                      <button
                        onClick={handleOpenCash}
                        className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium whitespace-nowrap"
                      >
                        <i className="ri-check-line mr-2"></i>
                        Ouvrir la Caisse
                      </button>
                    </div>

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex">
                        <i className="ri-information-line text-blue-600 text-xl mr-3 flex-shrink-0"></i>
                        <div className="text-sm text-blue-800">
                          <p className="font-medium mb-1">Important :</p>
                          <ul className="list-disc list-inside space-y-1">
                            <li>L'ouverture de caisse est obligatoire avant toute opération</li>
                            <li>Le solde d'ouverture doit correspondre au montant physique présent</li>
                            <li>Une fois ouverte, la caisse doit être clôturée en fin de journée</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Onglet Opérations */}
                {activeTab === 'operations' && currentSession && (
                  <div>
                    <div className="grid grid-cols-4 gap-6 mb-6">
                      <div className="bg-blue-50 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm text-blue-600 font-medium">Solde Actuel</span>
                          <i className="ri-wallet-3-line text-blue-600"></i>
                        </div>
                        <p className="text-2xl font-bold text-blue-900">
                          {theoreticalBalance.toFixed(2)} GNF
                        </p>
                      </div>
                      <div className="bg-green-50 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm text-green-600 font-medium">Encaissements</span>
                          <i className="ri-arrow-down-line text-green-600"></i>
                        </div>
                        <p className="text-2xl font-bold text-green-900">
                          +{totalEncaissements.toFixed(2)} GNF
                        </p>
                        <p className="text-xs text-green-600 mt-1">
                          {operations.filter(op => op.type === 'ENCAISSEMENT').length} opération(s)
                        </p>
                      </div>
                      <div className="bg-red-50 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm text-red-600 font-medium">Décaissements</span>
                          <i className="ri-arrow-up-line text-red-600"></i>
                        </div>
                        <p className="text-2xl font-bold text-red-900">
                          -{totalDecaissements.toFixed(2)} GNF
                        </p>
                        <p className="text-xs text-red-600 mt-1">
                          {operations.filter(op => op.type === 'DECAISSEMENT').length} opération(s)
                        </p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm text-gray-600 font-medium">Total Opérations</span>
                          <i className="ri-file-list-3-line text-gray-600"></i>
                        </div>
                        <p className="text-2xl font-bold text-gray-900">
                          {operations.length}
                        </p>
                      </div>
                    </div>

                    {!showOperationForm && (
                      <button
                        onClick={() => setShowOperationForm(true)}
                        className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium mb-6 whitespace-nowrap"
                      >
                        <i className="ri-add-line mr-2"></i>
                        Nouvelle Opération
                      </button>
                    )}

                    {showOperationForm && (
                      <div className="bg-gray-50 rounded-lg p-6 mb-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">
                          Nouvelle Opération de Caisse
                        </h3>

                        <div className="mb-4">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Type d'Opération *
                          </label>
                          <div className="grid grid-cols-2 gap-4">
                            <button
                              onClick={() => {
                                setOperationType('ENCAISSEMENT');
                                setOperationCategory('');
                                setOperationSubcategory('');
                              }}
                              className={`px-4 py-3 rounded-lg border-2 font-medium transition-colors whitespace-nowrap ${
                                operationType === 'ENCAISSEMENT'
                                  ? 'border-green-500 bg-green-50 text-green-700'
                                  : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                              }`}
                            >
                              <i className="ri-arrow-down-line mr-2"></i>
                              Encaissement (Entrée)
                            </button>
                            <button
                              onClick={() => {
                                setOperationType('DECAISSEMENT');
                                setOperationCategory('');
                                setOperationSubcategory('');
                              }}
                              className={`px-4 py-3 rounded-lg border-2 font-medium transition-colors whitespace-nowrap ${
                                operationType === 'DECAISSEMENT'
                                  ? 'border-red-500 bg-red-50 text-red-700'
                                  : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                              }`}
                            >
                              <i className="ri-arrow-up-line mr-2"></i>
                              Décaissement (Sortie)
                            </button>
                          </div>
                        </div>

                        <div className="mb-4">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Catégorie *
                          </label>
                          <select
                            value={operationCategory}
                            onChange={(e) => {
                              setOperationCategory(e.target.value);
                              setOperationSubcategory('');
                            }}
                            className="w-full px-4 py-2 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          >
                            <option value="">Sélectionnez une catégorie</option>
                            {(operationType === 'ENCAISSEMENT' ? encaissementCategories : decaissementCategories).map(cat => (
                              <option key={cat.value} value={cat.value}>
                                {cat.label}
                              </option>
                            ))}
                          </select>
                          {selectedCategory && (
                            <p className="text-sm text-gray-500 mt-1">
                              <i className="ri-information-line mr-1"></i>
                              {selectedCategory.description}
                            </p>
                          )}
                        </div>

                        {selectedCategory?.subcategories && (
                          <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Sous-catégorie
                            </label>
                            <select
                              value={operationSubcategory}
                              onChange={(e) => setOperationSubcategory(e.target.value)}
                              className="w-full px-4 py-2 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                              <option value="">Sélectionnez une sous-catégorie</option>
                              {selectedCategory.subcategories.map(sub => (
                                <option key={sub} value={sub}>
                                  {sub}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}

                        {selectedCategory?.requiresLink && (
                          <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Référence Document *
                            </label>
                            <input
                              type="text"
                              value={operationReference}
                              onChange={(e) => setOperationReference(e.target.value)}
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              placeholder="Ex: FAC-2025-00001"
                            />
                            <p className="text-sm text-gray-500 mt-1">
                              <i className="ri-link mr-1"></i>
                              Numéro de la facture ou du règlement associé
                            </p>
                          </div>
                        )}

                        <div className="mb-4">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Montant (GNF) *
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            value={operationAmount}
                            onChange={(e) => setOperationAmount(e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="0.00"
                          />
                        </div>

                        <div className="mb-4">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Description
                          </label>
                          <textarea
                            value={operationDescription}
                            onChange={(e) => setOperationDescription(e.target.value)}
                            rows={3}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Détails de l'opération..."
                          />
                        </div>

                        <div className="flex space-x-3">
                          <button
                            onClick={handleAddOperation}
                            className="flex-1 bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors font-medium whitespace-nowrap"
                          >
                            <i className="ri-check-line mr-2"></i>
                            Enregistrer l'Opération
                          </button>
                          <button
                            onClick={() => {
                              setShowOperationForm(false);
                              setOperationCategory('');
                              setOperationSubcategory('');
                              setOperationAmount('');
                              setOperationDescription('');
                              setOperationReference('');
                            }}
                            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium whitespace-nowrap"
                          >
                            <i className="ri-close-line mr-2"></i>
                            Annuler
                          </button>
                        </div>
                      </div>
                    )}

                    <div className="bg-white rounded-lg border border-gray-200">
                      <div className="px-6 py-4 border-b border-gray-200">
                        <h3 className="text-lg font-semibold text-gray-900">
                          Opérations de la Journée
                        </h3>
                      </div>
                      <div className="overflow-x-auto">
                        {operations.length === 0 ? (
                          <div className="text-center py-12">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-blue-600 mx-auto"></div>
                            <p className="text-gray-500 mt-4">Aucune opération enregistrée</p>
                          </div>
                        ) : (
                          <table className="w-full">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Heure</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Catégorie</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Référence</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Montant</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                              {operations.map((op) => (
                                <tr key={op.id} className="hover:bg-gray-50">
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {op.date_operation.substring(11, 16)}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
                                      op.type === 'ENCAISSEMENT'
                                        ? 'bg-green-100 text-green-800'
                                        : 'bg-red-100 text-red-800'
                                    }`}>
                                      <i className={`${op.type === 'ENCAISSEMENT' ? 'ri-arrow-down-line' : 'ri-arrow-up-line'} mr-1`}></i>
                                      {op.type === 'ENCAISSEMENT' ? 'Entrée' : 'Sortie'}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4 text-sm text-gray-900">
                                    <div>
                                      {(op.type === 'ENCAISSEMENT' ? encaissementCategories : decaissementCategories)
                                        .find(c => c.value === op.categorie)?.label || op.categorie}
                                    </div>
                                    {op.sous_categorie && (
                                      <div className="text-xs text-gray-500">{op.sous_categorie}</div>
                                    )}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {op.document_reference || '-'}
                                  </td>
                                  <td className="px-6 py-4 text-sm text-gray-500">
                                    {op.description || '-'}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                    <span className={op.type === 'ENCAISSEMENT' ? 'text-green-600' : 'text-red-600'}>
                                      {op.type === 'ENCAISSEMENT' ? '+' : '-'}{op.montant.toFixed(2)} GNF
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Onglet Clôture */}
                {activeTab === 'closing' && currentSession && (
                  <div className="max-w-3xl mx-auto">
                    <div className="text-center mb-8">
                      <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <i className="ri-door-closed-line text-4xl text-red-600"></i>
                      </div>
                      <h2 className="text-2xl font-bold text-gray-900 mb-2">
                        Clôture de Caisse
                      </h2>
                      <p className="text-gray-600">
                        Vérifiez le solde et clôturez la session de caisse
                      </p>
                    </div>

                    <div className="bg-gray-50 rounded-lg p-6 mb-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">
                        Résumé de la Journée
                      </h3>
                      <div className="space-y-3">
                        <div className="flex justify-between items-center py-2 border-b border-gray-200">
                          <span className="text-gray-600">Solde d'Ouverture</span>
                          <span className="font-semibold text-gray-900">
                            {currentSession.solde_ouverture.toFixed(2)} GNF
                          </span>
                        </div>
                        <div className="flex justify-between items-center py-2 border-b border-gray-200">
                          <span className="text-green-600">
                            <i className="ri-arrow-down-line mr-2"></i>
                            Total Encaissements
                          </span>
                          <span className="font-semibold text-green-600">
                            +{totalEncaissements.toFixed(2)} GNF
                          </span>
                        </div>
                        <div className="flex justify-between items-center py-2 border-b border-gray-200">
                          <span className="text-red-600">
                            <i className="ri-arrow-up-line mr-2"></i>
                            Total Décaissements
                          </span>
                          <span className="font-semibold text-red-600">
                            -{totalDecaissements.toFixed(2)} GNF
                          </span>
                        </div>
                        <div className="flex justify-between items-center py-3 bg-blue-50 px-4 rounded-lg">
                          <span className="font-semibold text-blue-900">Solde Théorique</span>
                          <span className="text-2xl font-bold text-blue-600">
                            {theoreticalBalance.toFixed(2)} GNF
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white rounded-lg border-2 border-gray-200 p-6 mb-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">
                        Comptage Physique de la Caisse
                      </h3>
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Solde Réel Compté (GNF) *
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={actualBalance}
                          onChange={(e) => setActualBalance(e.target.value)}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg"
                          placeholder="0.00"
                        />
                        <p className="text-sm text-gray-500 mt-2">
                          <i className="ri-information-line mr-1"></i>
                          Saisissez le montant exact présent dans la caisse après comptage
                        </p>
                      </div>

                      {actualBalance && (
                        <div className={`p-4 rounded-lg ${
                          Math.abs(difference) < 0.01
                            ? 'bg-green-50 border border-green-200'
                            : 'bg-red-50 border border-red-200'
                        }`}>
                          <div className="flex items-center justify-between mb-2">
                            <span className={`font-semibold ${
                              Math.abs(difference) < 0.01 ? 'text-green-900' : 'text-red-900'
                            }`}>
                              Écart Détecté
                            </span>
                            <span className={`text-2xl font-bold ${
                              Math.abs(difference) < 0.01 ? 'text-green-600' : 'text-red-600'
                            }`}>
                              {difference > 0 ? '+' : ''}{difference.toFixed(2)} GNF
                            </span>
                          </div>
                          {Math.abs(difference) < 0.01 ? (
                            <p className="text-sm text-green-700">
                              <i className="ri-check-line mr-1"></i>
                              Parfait ! Le solde est conforme.
                            </p>
                          ) : (
                            <p className="text-sm text-red-700">
                              <i className="ri-alert-line mr-1"></i>
                              {difference > 0 
                                ? 'Manque d\'argent dans la caisse'
                                : 'Excédent d\'argent dans la caisse'
                              }
                            </p>
                          )}
                        </div>
                      )}
                    </div>

                    {actualBalance && Math.abs(difference) > 0.01 && (
                      <div className="bg-yellow-50 rounded-lg border-2 border-yellow-200 p-6 mb-6">
                        <h3 className="text-lg font-semibold text-yellow-900 mb-4">
                          <i className="ri-alert-line mr-2"></i>
                          Justification de l'Écart (Obligatoire)
                        </h3>
                        <textarea
                          value={justification}
                          onChange={(e) => setJustification(e.target.value)}
                          rows={4}
                          className="w-full px-4 py-2 border border-yellow-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                          placeholder="Expliquez la raison de l'écart (erreur de saisie, oubli d'enregistrement, perte, etc.)..."
                        />
                      </div>
                    )}

                    <button
                      onClick={handleCloseCash}
                      disabled={closingLoading}
                      className="w-full bg-red-600 text-white px-6 py-4 rounded-lg hover:bg-red-700 transition-colors font-medium text-lg whitespace-nowrap disabled:opacity-70"
                    >
                      <i className="ri-lock-line mr-2"></i>
                      {closingLoading ? 'Clôture en cours...' : 'Clôturer la Caisse'}
                    </button>

                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mt-6">
                      <div className="flex">
                        <i className="ri-error-warning-line text-red-600 text-xl mr-3 flex-shrink-0"></i>
                        <div className="text-sm text-red-800">
                          <p className="font-medium mb-1">Attention :</p>
                          <ul className="list-disc list-inside space-y-1">
                            <li>La clôture est définitive et ne peut pas être annulée</li>
                            <li>Assurez-vous d'avoir enregistré toutes les opérations</li>
                            <li>Le solde réel doit être compté avec précision</li>
                            <li>Tout écart doit être justifié</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}